import { useEffect, useMemo } from 'react';
import { Button, Card, Form, Tabs } from 'antd';
import { observer } from 'mobx-react-lite';
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';

import { useResource } from 'app/common/api';
import { DataStore } from 'app/common/data-storage/features/data-storage';
import { NotificationsRender } from 'app/common/notifications/features/notifications';
import { usePageNotify } from 'app/common/notifications/features/page-notifications';
import { useInstance } from 'app/libs/use-instance';
import { CanAccess } from 'app/railways/domains/access/features/permissions';
import { Loading, PageContent, Stack } from 'app/ui';

import { TTripDetails } from '../../core/trip-details';
import { TTrip, TWagon, TWagonSeat } from '../../core/trips';
import { getTripDetailsRequest } from '../../requests/get-trip-details.request';
import { updateTripRequest } from '../../requests/update-trip.request';
import { createTripInitialState } from './create-trip-initial-state';
import { ErrorFieldsStatusProvider, useErrorFieldsStatus } from './form-utils';
import { RouteCard } from './route-card';
import { TicketsCard } from './tickets-card';
import { TripStatusCard } from './trip-status-card';
import { SeatRow, WagonScheme } from './wagon-scheme';

type DetailsTripProps = {
  tripId: number;
};
export const DetailsTrip = observer((props: DetailsTripProps) => {
  const pageNotify = usePageNotify();
  const [form] = Form.useForm();

  const errorFieldsStatus = useErrorFieldsStatus();

  const dataStore = useInstance(
    () => new DataStore<TTripDetails>(createTripInitialState()),
  );

  const getTripResource = useResource<{ id: TTrip['id'] }, TTripDetails>({
    request: getTripDetailsRequest,
    errorNotify: pageNotify,
    onDone: (orderData) => {
      dataStore.setData(orderData);
      form.setFieldsValue(orderData.trip);
    },
  });

  const updateTripResource = useResource<
    { trip: Partial<TTrip>; seats?: TWagonSeat[] },
    TTripDetails
  >({
    request: updateTripRequest,
    errorNotify: pageNotify,
    onDone: (orderData) => {
      dataStore.setData(orderData);
      form.setFieldsValue(orderData.trip);
      pageNotify.addMessage({
        type: 'success',
        message: 'Данные рейса успешно обновлены',
      });
    },
  });

  const handleSubmit = (data: Partial<TTrip>) => {
    form.validateFields().then(() => {
      pageNotify.clearAll();
      errorFieldsStatus.reset();
      updateTripResource.send({ trip: { ...dataStore.data.trip, ...data } });
    });
  };

  const handleSeatsUpdate = (seats: SeatRow[]) => {
    const preparedSeats = seats.map((seat) => ({
      ...seat,
      tripId: dataStore.data.trip?.id,
    }));

    updateTripResource.send({
      trip: dataStore.data.trip,
      seats: preparedSeats,
    });
  };

  const handleFinishFailed = (errorInfo: ValidateErrorEntity) => {
    errorFieldsStatus.setErrorFields(errorInfo.errorFields);
  };

  useEffect(() => {
    if (!props.tripId) return;

    getTripResource.send({ id: props.tripId });
  }, [getTripResource, props.tripId]);

  useEffect(() => {
    if (errorFieldsStatus.errorFields.length > 0)
      pageNotify.addMessage({
        type: 'error',
        message: 'Проверьте обязательные поля',
      });
  }, [errorFieldsStatus.errorFields.length, pageNotify]);

  const trip = dataStore.data.trip;
  const seats = dataStore.data.seats;
  const wagons = dataStore.data.trip?.wagons;
  const trainTypes = dataStore.data.trainTypes;
  const loading = getTripResource.isLoading || updateTripResource.isLoading;

  const wagonSeats = useMemo<TWagon[]>(() => {
    if (!wagons || (Array.isArray(wagons) && wagons.length === 0)) return [];

    const wagonsWithSeats = wagons.map((wagon) => ({
      ...wagon,
      seats: seats.filter((seat) => seat.wagonNum === wagon.number),
    }));

    return wagonsWithSeats;
  }, [seats, wagons]);

  return (
    <Loading active={loading}>
      <Tabs
        style={{ marginTop: 1 }}
        tabBarStyle={{ margin: 0, background: 'white', padding: '0 16px' }}
      >
        <Tabs.TabPane tab="Подробная информация" key="details">
          <PageContent>
            <Form
              layout="vertical"
              initialValues={trip}
              form={form}
              onFinish={handleSubmit}
              onFinishFailed={handleFinishFailed}
            >
              <ErrorFieldsStatusProvider value={errorFieldsStatus}>
                <Stack>
                  <TripStatusCard trip={trip} />
                  <RouteCard trip={trip} trainTypes={trainTypes} />
                  <TicketsCard />

                  <NotificationsRender notifyStore={pageNotify} />

                  <CanAccess perm="PERM_ROUTE_TRIP_UPDATE">
                    <Card size="small" bodyStyle={{ display: 'flex' }}>
                      <Button
                        htmlType="submit"
                        type="primary"
                        style={{ marginLeft: 'auto' }}
                      >
                        Сохранить
                      </Button>
                    </Card>
                  </CanAccess>
                </Stack>
              </ErrorFieldsStatusProvider>
            </Form>
          </PageContent>
        </Tabs.TabPane>

        {wagonSeats.length > 0 ? (
          <Tabs.TabPane tab="Схема вагонов" key="wagons">
            <PageContent style={{ marginBottom: 16 }}>
              <WagonScheme wagons={wagonSeats} onChange={handleSeatsUpdate} />
            </PageContent>
          </Tabs.TabPane>
        ) : null}
      </Tabs>
    </Loading>
  );
});
